import Reveal from "@/components/Reveal";
import { SunMark } from "@/components/Logo";
import { IMAGES } from "@/lib/site";

const TENETS = [
  {
    n: "01",
    title: "Presence before answers",
    body: "Most hard moments don't need a fix as much as they need someone to stay in the room. The listening comes first.",
  },
  {
    n: "02",
    title: "The patient leads",
    body: "It is the person's illness, the person's life, and the person's goodbye. Everyone else is a guest who walks alongside.",
  },
  {
    n: "03",
    title: "Grief keeps no clock",
    body: "There are no stages to complete and no date to be finished. Love lasts longer than loss, and that is allowed to take time.",
  },
];

export default function About() {
  return (
    <div className="bg-ivory">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-20 sm:pt-44">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-16 items-start">
          <div className="lg:col-span-8 lg:order-2">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                About Chaplain Derrick
              </p>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
                Beside Patients and Families, From Diagnosis to Goodbye
              </h1>
              <p className="mt-6 font-serif italic text-lg sm:text-xl text-gold max-w-xl">
                Restoring the patient&rsquo;s voice through every stage of the
                journey.
              </p>
            </Reveal>
            <div className="mt-9 space-y-6 text-lg text-stone2 leading-relaxed">
              <Reveal delay={0.08}>
                <p>
                  Derrick Hall is a hospice chaplain, author, and educator
                  with 10 years of experience in hospice care. He walks beside
                  people living with serious illness and the families who love
                  them, from diagnosis through care, goodbye, and the grief
                  that follows.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2
                  className="pt-6 font-serif text-2xl sm:text-3xl text-navy tracking-tight"
                  data-testid="about-heading-story"
                >
                  The Story Behind the Book
                </h2>
              </Reveal>
              <Reveal delay={0.12}>
                <p>
                  From Diagnosis to Goodbye&trade; was three years in the
                  making, born from personal loss, professional experience,
                  and a growing conviction that every patient deserves to be
                  heard.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <p>
                  The journey began with the loss of Derrick&rsquo;s brother,
                  David, following a battle with cancer, and continued with
                  the loss of his mother while receiving hospice care. Both
                  experiences left a lasting impression, but it was caring for
                  his mother during her final journey that gave Derrick a
                  deeper understanding of something he had not fully
                  recognized before.
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <p>
                  As her son and caregiver, Derrick worked to honor the wishes
                  she had expressed in her advance directive. Yet through that
                  experience, he came to understand an important distinction:
                  honoring a patient&rsquo;s documented wishes must never
                  replace listening to the patient while they are still able
                  to speak for themselves.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p>
                  An advance directive has an important place in healthcare.
                  It helps guide decisions when a person can no longer make or
                  communicate those decisions. But while a patient remains
                  able to express their wishes and participate in decisions,
                  their voice must remain central.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p>
                  After his mother&rsquo;s passing, Derrick began paying even
                  closer attention to the patients and families he served as a
                  hospice chaplain. He noticed how easily a patient&rsquo;s
                  voice could become overshadowed during serious
                  illness&mdash;not necessarily through neglect, but often
                  through the loving intentions of family members, the
                  urgency of medical decisions, and the emotional weight of
                  caregiving.
                </p>
              </Reveal>
              <Reveal delay={0.22}>
                <p>
                  He began asking himself a question: Are we truly hearing the
                  person at the center of this journey?
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2
                  className="pt-6 font-serif text-2xl sm:text-3xl text-navy tracking-tight"
                  data-testid="about-heading-purpose"
                >
                  From Personal Experience to a Greater Purpose
                </h2>
              </Reveal>
              <Reveal delay={0.12}>
                <p>
                  Wanting to help families recognize what he had come to
                  understand, Derrick searched for a book that addressed the
                  importance of preserving the patient&rsquo;s voice
                  throughout the journey of serious illness, hospice care, and
                  the decisions that accompany it.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <p>
                  Unable to find a resource that approached the subject in the
                  way he envisioned, he decided to write one himself.
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <p>
                  Although his mother&rsquo;s hospice experience inspired the
                  book, Derrick intentionally chose not to make From Diagnosis
                  to Goodbye&trade; a story about his personal journey with
                  her.
                </p>
              </Reveal>
              <Reveal delay={0.18}>
                <p>
                  His purpose was something greater: to help patients,
                  families, caregivers, and healthcare professionals recognize
                  that the person receiving care must never become lost in the
                  process of providing it.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p>
                  The book invites readers to listen more intentionally, ask
                  meaningful questions, respect personal wishes, and create
                  space for patients to express their fears, hopes, concerns,
                  values, and preferences throughout every stage of the
                  journey.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2
                  className="pt-6 font-serif text-2xl sm:text-3xl text-navy tracking-tight"
                  data-testid="about-heading-voice"
                >
                  A Voice Worth Hearing
                </h2>
              </Reveal>
              <Reveal delay={0.12}>
                <p>
                  Today, Derrick continues this mission through hospice
                  chaplaincy, writing, education, and resources designed to
                  encourage compassionate conversations.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <p>
                  His companion workbooks and study guides extend the message
                  of From Diagnosis to Goodbye&trade; into personal
                  reflection, family discussions, and guided learning.
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <p>At the heart of his work is a simple conviction:</p>
              </Reveal>
              <Reveal delay={0.18}>
                <p
                  className="border-l-2 border-gold/70 pl-6 font-serif text-2xl sm:text-3xl text-navy leading-snug"
                  data-testid="about-final-statement"
                >
                  The patient&rsquo;s voice should never become the quietest
                  voice in the room.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="font-serif italic text-gold">
                  See the person. Hear the voice. Honor the journey.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-4 order-first lg:order-1">
            <Reveal delay={0.15}>
              <div
                className="relative aspect-[4/5] rounded-md border border-line bg-sand overflow-hidden"
                data-testid="about-portrait"
              >
                <div
                  className="absolute inset-0 z-10 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(11,21,34,0) 62%, rgba(11,21,34,0.28) 100%)",
                  }}
                  aria-hidden="true"
                />
                <img
                  src="/images/about-portrait.jpg"
                  alt="Chaplain Derrick Hall, author of From Diagnosis to Goodbye"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <p className="mt-4 text-xs text-mist text-center">
                Chaplain Derrick Hall
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-sand border-y border-line">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-24">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
              How Derrick works
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-10">
            {TENETS.map((t, i) => (
              <Reveal key={t.n} delay={0.08 * i}>
                <div className="border-t-2 border-gold/70 pt-6">
                  <p className="font-serif text-gold text-lg">{t.n}</p>
                  <h3 className="mt-3 font-serif text-xl text-navy">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-stone2 leading-relaxed text-[15px]">
                    {t.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative h-[56vh] min-h-[380px] overflow-hidden">
        <img
          src={IMAGES.couple}
          alt="An elderly couple sitting close together, holding hands"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-navy-deep/65" />
        <div className="relative h-full flex items-center justify-center px-6">
          <Reveal>
            <p className="max-w-3xl text-center font-serif text-2xl sm:text-4xl leading-snug text-ivory">
              Listening first. Speaking carefully. Always leaving room for the
              patient&rsquo;s voice.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
